'use client';

import React, { useState, useEffect } from 'react';
import { Plus, Copy, Eye, Search, X, FileText } from 'lucide-react';
import { useAppraisalStore } from '@/store/appraisalStore';
import { MOCK_TEMPLATES } from '@/data/mockData';
import type { AppraisalTemplate } from '@/store/appraisalStore';
import TemplateBuilderCanvas from './TemplateBuilderCanvas';
import TemplatePreviewModal from './TemplatePreviewModal';
import ToastContainer, { useToast } from '@/components/ToastContainer';

const STATUS_CLASS: Record<string, string> = {
  active: 'pn-appraisal__status-badge--active',
  draft: 'pn-appraisal__status-badge--draft',
  archived: 'pn-appraisal__status-badge--archived',
};

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  draft: 'Draft',
  archived: 'Archived',
};

export default function TemplateManagementClient() {
  const { templates, setTemplates, addTemplate, updateTemplate } = useAppraisalStore();
  const { toasts, addToast, removeToast } = useToast();
  const [search, setSearch] = useState('');
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<AppraisalTemplate | null>(null);

  useEffect(() => {
    if (!templates.length) {
      setTemplates(MOCK_TEMPLATES);
      setActiveTemplateId(MOCK_TEMPLATES[0]?.id || null);
    } else if (!activeTemplateId) {
      setActiveTemplateId(templates[0]?.id || null);
    }
  }, [templates.length, setTemplates, activeTemplateId]);

  const filtered = templates.filter((t) =>
    !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.description.toLowerCase().includes(search.toLowerCase())
  );

  const activeTemplate = templates.find((t) => t.id === activeTemplateId) || null;

  const handleClone = (tmpl: AppraisalTemplate) => {
    const cloned: AppraisalTemplate = {
      ...tmpl,
      id: `tmpl-${Date.now()}`,
      name: `${tmpl.name} (Copy)`,
      status: 'draft',
      version: 'v1.0',
      usedInCycles: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    addTemplate(cloned);
    setActiveTemplateId(cloned.id);
    addToast('success', `Template cloned as "${cloned.name}"`);
  };

  const handleCreateNew = () => {
    const newTmpl: AppraisalTemplate = {
      id: `tmpl-${Date.now()}`,
      name: 'New Template',
      description: 'Enter a description for this template',
      version: 'v1.0',
      status: 'draft',
      sections: [],
      createdBy: 'Priya Nair',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      usedInCycles: 0,
    };
    addTemplate(newTmpl);
    setActiveTemplateId(newTmpl.id);
    addToast('success', 'New template created — add sections and questions below');
  };

  return (
    <>
      {/* Page Header */}
      <div className="pn-appraisal__page-header">
        <div className="pn-appraisal__page-header-left">
          <h1 className="pn-appraisal__page-header-title">Templates & Question Management</h1>
          <p className="pn-appraisal__page-header-subtitle">
            {templates.length} templates · Build and manage reusable appraisal form templates
          </p>
        </div>
        <div className="pn-appraisal__page-header-actions">
          <button
            className="pn-appraisal__btn pn-appraisal__btn--secondary pn-appraisal__btn--sm"
            onClick={() => activeTemplate && setPreviewTemplate(activeTemplate)}
            disabled={!activeTemplate}
          >
            <Eye size={14} /> Preview Form
          </button>
          <button
            className="pn-appraisal__btn pn-appraisal__btn--primary pn-appraisal__btn--sm"
            onClick={handleCreateNew}
          >
            <Plus size={14} /> New Template
          </button>
        </div>
      </div>

      {/* Builder layout */}
      <div className="pn-appraisal__question-builder">
        {/* Left sidebar — template list */}
        <div className="pn-appraisal__question-builder-sidebar">
          <div className="pn-appraisal__question-builder-sidebar-header">
            <span className="pn-appraisal__question-builder-sidebar-header-title">
              Templates ({filtered.length})
            </span>
            <button
              className="pn-appraisal__btn pn-appraisal__btn--primary pn-appraisal__btn--icon"
              onClick={handleCreateNew}
              data-tooltip="New template"
              aria-label="Create new template"
            >
              <Plus size={14} />
            </button>
          </div>

          {/* Search */}
          <div style={{ padding: '8px 8px 0' }}>
            <div className="pn-appraisal__filter-bar-search" style={{ minWidth: 'auto' }}>
              <Search size={13} />
              <input
                type="text"
                placeholder="Search templates…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search templates"
              />
              {search && (
                <button onClick={() => setSearch('')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                  <X size={11} color="var(--pn-text-muted)" />
                </button>
              )}
            </div>
          </div>

          <div className="pn-appraisal__question-builder-sidebar-list">
            {filtered.length === 0 ? (
              <div className="pn-appraisal__empty" style={{ padding: 24 }}>
                <div className="pn-appraisal__empty-icon" style={{ width: 40, height: 40, marginBottom: 10 }}>
                  <FileText size={20} />
                </div>
                <div className="pn-appraisal__empty-title" style={{ fontSize: 13 }}>No templates found</div>
                <p className="pn-appraisal__empty-desc" style={{ fontSize: 12 }}>
                  No templates match your search. Create a new one to get started.
                </p>
              </div>
            ) : (
              filtered.map((tmpl) => (
                <div
                  key={tmpl.id}
                  className={`pn-appraisal__question-builder-template-item${activeTemplateId === tmpl.id ? ' pn-appraisal__question-builder-template-item--active' : ''}`}
                  onClick={() => setActiveTemplateId(tmpl.id)}
                  role="button"
                  tabIndex={0}
                  aria-pressed={activeTemplateId === tmpl.id}
                  onKeyDown={(e) => e.key === 'Enter' && setActiveTemplateId(tmpl.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6 }}>
                    <div className="pn-appraisal__question-builder-template-item-name">{tmpl.name}</div>
                    <span className="pn-appraisal__question-builder-template-item-version">{tmpl.version}</span>
                  </div>
                  <div className="pn-appraisal__question-builder-template-item-meta">
                    <span className={`pn-appraisal__status-badge ${STATUS_CLASS[tmpl.status]}`} style={{ fontSize: 10, padding: '1px 7px' }}>
                      {STATUS_LABEL[tmpl.status]}
                    </span>
                    <span>{tmpl.sections.length} sections</span>
                    <span>{tmpl.sections.reduce((acc, s) => acc + s.questions.length, 0)} questions</span>
                  </div>
                  <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                    <button
                      className="pn-appraisal__btn pn-appraisal__btn--ghost pn-appraisal__btn--sm"
                      style={{ fontSize: 11, padding: '3px 8px' }}
                      onClick={(e) => { e.stopPropagation(); setPreviewTemplate(tmpl); }}
                      aria-label="Preview template"
                    >
                      <Eye size={11} /> Preview
                    </button>
                    <button
                      className="pn-appraisal__btn pn-appraisal__btn--ghost pn-appraisal__btn--sm"
                      style={{ fontSize: 11, padding: '3px 8px' }}
                      onClick={(e) => { e.stopPropagation(); handleClone(tmpl); }}
                      aria-label="Clone template"
                    >
                      <Copy size={11} /> Clone
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right canvas — question builder */}
        <div className="pn-appraisal__question-builder-canvas">
          {activeTemplate ? (
            <TemplateBuilderCanvas
              template={activeTemplate}
              onUpdate={(updated) => updateTemplate(updated.id, updated)}
              onToast={addToast}
            />
          ) : (
            <div className="pn-appraisal__empty" style={{ height: '100%', justifyContent: 'center' }}>
              <div className="pn-appraisal__empty-icon">
                <FileText size={26} />
              </div>
              <div className="pn-appraisal__empty-title">No template selected</div>
              <p className="pn-appraisal__empty-desc">
                Select a template from the left panel to start editing, or create a new one.
              </p>
              <button className="pn-appraisal__btn pn-appraisal__btn--primary pn-appraisal__btn--sm" onClick={handleCreateNew}>
                <Plus size={13} /> Create New Template
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
        />
      )}

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </>
  );
}